import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProducts } from '@/hooks/useProducts';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import CartDrawer from '@/components/CartDrawer';
import { Search, User, ShoppingBag, Menu, X, ArrowLeft, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';

const CATEGORIES = ['Screen guard', 'Camera ring', 'Charger cable', 'Airpods'];

const ProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { products, loading, error } = useProducts();
  const { getCartCount, setIsCartOpen } = useCart();
  const { currentUser, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [showFilters, setShowFilters] = useState(false);
  const cartCount = getCartCount();

  // No login required to view products - removed authentication requirement

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleTripleClick = () => {
    setClickCount((prev) => prev + 1);
    if (clickTimer) clearTimeout(clickTimer);
    const timer = setTimeout(() => setClickCount(0), 1000);
    setClickTimer(timer);
    if (clickCount + 1 === 3) {
      navigate('/admin/login');
      setClickCount(0);
    }
  };

  const handleCartClick = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    setIsCartOpen(true);
  };

  // Calculate discount percentage
  const getDiscountPercentage = (original, discount) => {
    if (!discount || discount >= original) return 0;
    return Math.round(((original - discount) / original) * 100);
  };

  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm === '' || 
      product.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.modelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || 
      product.category?.toLowerCase() === selectedCategory.toLowerCase();
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500 font-light">Loading products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4 font-light">Error loading products</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-black text-white text-sm font-light tracking-wide"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <CartDrawer />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            {/* Left - Empty */}
            <div className="hidden md:flex items-center space-x-8 w-32">
              {/* Removed Catalog and Contact */}
            </div>

            <button className="md:hidden text-black" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer" onClick={handleLogoClick} onDoubleClick={handleTripleClick}>
              <img 
                src="https://customer-assets.emergentagent.com/job_luxury-estore-3/artifacts/czobo02e_B%26C%20LOGO%20-%2001.png" 
                alt="B&C Store" 
                className="h-10 sm:h-12 object-contain hover:opacity-80 transition-opacity"
              />
            </div>

            <div className="flex items-center space-x-4 sm:space-x-5">
              {currentUser ? (
                <div className="relative">
                  <button onClick={() => setShowUserMenu(!showUserMenu)} className="text-gray-600 hover:text-black transition-colors">
                    <User size={20} strokeWidth={1.5} />
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                      <button onClick={() => { navigate('/profile'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Profile</button>
                      <button onClick={() => { navigate('/my-orders'); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light">My Orders</button>
                      <button onClick={async () => { await logout(); setShowUserMenu(false); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 font-light">Logout</button>
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => navigate('/login')} className="text-gray-600 hover:text-black transition-colors">
                  <User size={20} strokeWidth={1.5} />
                </button>
              )}
              
              <button onClick={handleCartClick} className="text-gray-600 hover:text-black transition-colors relative">
                <ShoppingBag size={20} strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-light">{cartCount}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">Home</button>
              <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">My Profile</button>
              <button onClick={() => { navigate('/my-orders'); setMobileMenuOpen(false); }} className="block text-gray-600 text-base font-light">My Orders</button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Main Content */}
      <div className="pt-20 sm:pt-24 pb-16 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1800px] mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-500 hover:text-black mb-6 sm:mb-8 transition-colors font-light text-sm"
          >
            <ArrowLeft size={18} strokeWidth={1.5} />
            <span>Back to Home</span>
          </button>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light mb-3 tracking-wide">
              {selectedCategory !== 'all' ? selectedCategory : 'Our Collection'}
            </h1>
            <p className="text-gray-500 text-sm sm:text-base font-light">
              Premium mobile accessories ({filteredProducts.length} products)
            </p>
          </motion.div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            {/* Search Bar */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} strokeWidth={1.5} />
              <Input
                type="text"
                placeholder="Search products, models..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-200 focus:border-black focus:ring-0 font-light bg-white"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 text-sm font-light rounded-full transition-colors ${
                  selectedCategory === 'all' 
                    ? 'bg-black text-white' 
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
                }`}
              >
                All
              </button>
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 text-sm font-light rounded-full transition-colors ${
                    selectedCategory === cat 
                      ? 'bg-black text-white' 
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-black'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid - 2x2 on mobile */}
          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 font-light">No products found matching your search.</p>
              <button
                onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
                className="mt-4 px-6 py-2 bg-black text-white text-sm font-light tracking-wide"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
              {filteredProducts.map((product, index) => {
                const originalPrice = product.originalPrice || product.price;
                const discountPrice = product.discountPrice;
                const discountPercent = getDiscountPercentage(originalPrice, discountPrice);
                const displayPrice = discountPrice || originalPrice;
                const colors = product.colors ? (typeof product.colors === 'string' ? product.colors.split(',').map(c => c.trim()) : product.colors) : [];
                
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/product/${product.id}`)}
                    data-testid={`product-card-${index}`}
                  >
                    {/* Product Image */}
                    <div className="relative aspect-square bg-white overflow-hidden mb-3 rounded-2xl border border-gray-100">
                      <img
                        src={product.images?.[0] || 'https://via.placeholder.com/400'}
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-2xl"
                      />
                      
                      {/* Discount Badge */}
                      {discountPercent > 0 && (
                        <div className="absolute top-2 left-2 bg-black text-white text-[10px] sm:text-xs px-2 py-1 font-light rounded-md">
                          {discountPercent}% OFF
                        </div>
                      )}

                      {/* Quick View on Hover */}
                      <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 hidden sm:block">
                        <button className="w-full py-2.5 bg-white/95 backdrop-blur-sm text-black text-xs sm:text-sm font-light tracking-wide hover:bg-white transition-colors rounded-lg">
                          Quick View
                        </button>
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="space-y-1.5">
                      {/* Category & Model */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {product.category && (
                          <span className="text-[10px] sm:text-xs text-gray-400 font-light uppercase tracking-wider">
                            {product.category}
                          </span>
                        )}
                        {product.modelName && (
                          <span className="text-[10px] sm:text-xs text-gray-400 font-light">
                            • {product.modelName}
                          </span>
                        )}
                      </div>

                      {/* Product Name */}
                      <h3 className="text-xs sm:text-sm font-normal line-clamp-2 text-gray-900">
                        {product.title}
                      </h3>

                      {/* Price - Fixed display */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {discountPrice && discountPrice < originalPrice ? (
                          <>
                            <span className="text-xs sm:text-sm text-gray-400 line-through font-light">₹{originalPrice}</span>
                            <span className="text-sm sm:text-base font-medium text-black">₹{discountPrice}</span>
                          </>
                        ) : (
                          <span className="text-sm sm:text-base font-medium text-black">₹{originalPrice}</span>
                        )}
                      </div>

                      {/* Colors Preview */}
                      {colors.length > 0 && (
                        <div className="flex items-center gap-1 pt-1">
                          <span className="text-[10px] text-gray-400 font-light">Colors:</span>
                          <div className="flex gap-1">
                            {colors.slice(0, 4).map((color, idx) => (
                              <div
                                key={idx}
                                className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border border-gray-200"
                                style={{ 
                                  backgroundColor: color.toLowerCase() === 'white' ? '#fff' : 
                                                 color.toLowerCase() === 'black' ? '#000' :
                                                 color.toLowerCase() === 'red' ? '#ef4444' :
                                                 color.toLowerCase() === 'blue' ? '#3b82f6' :
                                                 color.toLowerCase() === 'green' ? '#22c55e' :
                                                 color.toLowerCase() === 'gold' ? '#d4af37' :
                                                 color.toLowerCase() === 'silver' ? '#c0c0c0' :
                                                 color.toLowerCase() === 'pink' ? '#ec4899' :
                                                 color.toLowerCase() === 'purple' ? '#a855f7' :
                                                 color.toLowerCase() === 'orange' ? '#f97316' :
                                                 color.toLowerCase() === 'gray' || color.toLowerCase() === 'grey' ? '#6b7280' :
                                                 color.toLowerCase()
                                }}
                                title={color}
                              />
                            ))}
                            {colors.length > 4 && (
                              <span className="text-[10px] text-gray-400">+{colors.length - 4}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
